import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ChevronRight, 
  Edit, 
  GitBranch, 
  GitPullRequest, 
  Users, 
  GitFork,
  BarChart3,
  Calendar,
  Filter,
  Download,
  Settings,
  Eye,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  Plus,
  X
} from 'lucide-react';
import { 
  getPrompt, 
  getPromptLogs, 
  togglePromptLogging,
  getBranches,
  createBranch
} from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import PromptEvaluationSettings from '../components/PromptEvaluationSettings';
import PromptTabs from '../components/PromptTabs';

// Simple SVG icons to replace emojis  
const ChartIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const TreeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h1a2 2 0 011 1v1m-3 2v6a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
  </svg>
);

const LightningIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const PromptLogs = ({ user }) => {
  const { promptId } = useParams();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [showEvaluationSettings, setShowEvaluationSettings] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    from_date: '',
    to_date: '',
    model: '',
    branch_name: '',
    version: ''
  });
  const [toggleLoading, setToggleLoading] = useState(false);

  useEffect(() => {
    loadPrompt();
  }, [promptId]);

  useEffect(() => {
    if (prompt) {
      loadLogs();
    }
  }, [prompt, filters]);

  const loadPrompt = async () => {
    try {
      const promptData = await getPrompt(promptId);
      setPrompt(promptData);
    } catch (error) {
      console.error('Error loading prompt:', error);
    }
  };

  const loadLogs = async () => {
    if (!prompt) return;
    
    try {
      setLoading(true);
      const data = await getPromptLogs(promptId, filters);
      setLogs(data.logs || []);
      setAnalytics(data.analytics || null);
      setPagination(data.pagination || {});
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
      setAnalyticsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleToggleLogging = async () => {
    if (!prompt) return;
    
    try {
      setToggleLoading(true);
      await togglePromptLogging(promptId, !prompt.enable_logging);
      setPrompt(prev => ({ 
        ...prev, 
        enable_logging: !prev.enable_logging 
      }));
    } catch (error) {
      console.error('Error toggling logging:', error);
    } finally {
      setToggleLoading(false);
    }
  };

  const handleEvaluationClick = (log) => {
    // Navigate to the detailed evaluation page
    navigate(`/prompts/${promptId}/logs/${log.id}/evaluation`);
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const parseMetadata = (metadata) => {
    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch {
        return {};
      }
    }
    return metadata || {};
  };

  const AnalyticsCard = ({ title, value, subtitle, iconType }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-celadon-100 rounded-lg flex items-center justify-center">
            {iconType === 'chart' && <ChartIcon />}
            {iconType === 'refresh' && <RefreshIcon />}
            {iconType === 'tree' && <TreeIcon />}
            {iconType === 'lightning' && <LightningIcon />}
            {iconType === 'calendar' && <CalendarIcon />}
          </div>
        </div>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-500">{title}</div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{subtitle}</div>
        </div>
      </div>
    </div>
  );

  if (!prompt) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">Logs</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Usage Logs</h1>
          <p className="text-gray-600 mt-2">
            Real-world usage data for {prompt.name}
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => setShowEvaluationSettings(true)}
            className="btn-secondary"
          >
            <Settings className="h-4 w-4 mr-2" />
            Evaluation Settings
          </button>
          <button className="btn-secondary">
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Collaboration Navigation Tabs */}
      <PromptTabs promptId={promptId} />

      {/* Analytics Cards */}
      {!analyticsLoading && analytics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <AnalyticsCard
            title="Total Logs"
            value={analytics.total_logs || 0}
            subtitle="Last 30 days"
            iconType="chart"
          />
          <AnalyticsCard
            title="Unique Versions"
            value={analytics.unique_versions || 0}
            subtitle="Different versions used"
            iconType="refresh"
          />
          <AnalyticsCard
            title="Unique Branches"
            value={analytics.unique_branches || 0}
            subtitle="Different branches used"
            iconType="tree"
          />
          <AnalyticsCard
            title="Avg Response Time"
            value={'N/A'}
            subtitle="Execution time"
            iconType="lightning"
          />
          <AnalyticsCard
            title="Active Days"
            value={analytics.active_days || 0}
            subtitle="Days with activity"
            iconType="calendar"
          />
        </div>
      )}

      {/* Filters */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Branch Name
            </label>
            <input
              type="text"
              value={filters.branch_name}
              onChange={(e) => handleFilterChange('branch_name', e.target.value)}
              placeholder="e.g., main, feature-branch"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model
            </label>
            <input
              type="text"
              value={filters.model}
              onChange={(e) => handleFilterChange('model', e.target.value)}
              placeholder="e.g., gpt-4"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange('from_date', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange('to_date', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Per Page
            </label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Execution Logs
            {pagination.total && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({pagination.total} total)
              </span>
            )}
          </h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-celadon-500"></div>
            <p className="mt-2 text-sm text-gray-600">Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No logs found for this prompt</p>
            <p className="text-sm text-gray-400">
              {prompt.enable_logging 
                ? "Logs will appear here when the SDK is used with this prompt"
                : "Enable logging to start collecting usage data"
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version / Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Input
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Output
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auto-eval
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Key
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => {
                  const metadata = parseMetadata(log.metadata);
                  return (
                    <tr 
                      key={log.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleEvaluationClick(log)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(log.logged_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>
                          {log.version && (
                            <div className="font-medium">{log.version}</div>
                          )}
                          {log.branch_name && (
                            <div className="text-xs text-gray-500">{log.branch_name}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div 
                          className="hover:text-celadon-600"
                          title={log.input_text}
                        >
                          {truncateText(log.input_text, 80)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div 
                          className="hover:text-celadon-600"
                          title={log.output_text}
                        >
                          {truncateText(log.output_text, 80)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.model || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDuration(log.execution_time_ms)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.auto_eval_status ? (
                          <div className="space-y-1">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                log.auto_eval_status === 'pass' ? 'badge-status-success' :
                                log.auto_eval_status === 'fail' ? 'badge-status-error' :
                                log.auto_eval_status === 'pending' ? 'badge-status-info' :
                                'badge-status-info'
                              }`}
                            >
                              {log.auto_eval_status === 'pass' ? 'Pass' : 
                               log.auto_eval_status === 'fail' ? 'Fail' : 
                               log.auto_eval_status === 'pending' ? 'Pending' : 
                               log.auto_eval_status}
                            </span>
                            {log.auto_eval_status === 'pass' && log.auto_eval_total_count > 0 && (
                              <div className="text-xs text-gray-500">
                                <div className="text-celadon-600">
                                  {log.auto_eval_pass_count}/{log.auto_eval_total_count} passed
                                </div>
                                {log.auto_eval_avg_score && (
                                  <div>Avg: {(log.auto_eval_avg_score * 100).toFixed(0)}%</div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.key_name || 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
                {pagination.total && (
                  <span> ({pagination.total} total logs)</span>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Evaluation Settings Modal */}
      {showEvaluationSettings && (
        <PromptEvaluationSettings
          promptId={promptId}
          onClose={() => setShowEvaluationSettings(false)}
        />
      )}
    </div>
  );
};

export default PromptLogs; 